import React, { useEffect, useState, useRef } from 'react';
import { deathApi } from '../services/api';
import { Death } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalSelectorModal, { SelectedAnimal } from '../components/AnimalSelectorModal';
import ClearableInput from '../components/ClearableInput';
import Pagination from '../components/Pagination';

const Deaths: React.FC = () => {
  const { t } = useLanguage();
  const [deaths, setDeaths] = useState<Death[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAnimalSelector, setShowAnimalSelector] = useState(false);
  const [addAnimalDisplay, setAddAnimalDisplay] = useState('');
  const [editAnimalDisplay, setEditAnimalDisplay] = useState('');
  const [editingDeath, setEditingDeath] = useState<Death | null>(null);
  const [deletingDeath, setDeletingDeath] = useState<Death | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [newDeath, setNewDeath] = useState<Partial<Death>>({
    animal_id: 0,
    death_date: new Date().toISOString().split('T')[0],
    cause: '',
    location: '',
    disposal_method: '',
    veterinary_certificate: '',
    estimated_weight: 0,
    notes: '',
  });

  // Filters
  const [filterEarTag, setFilterEarTag] = useState('');
  const [filterCause, setFilterCause] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Debounced text filters
  const [debouncedEarTag, setDebouncedEarTag] = useState('');
  const [debouncedCause, setDebouncedCause] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEarTag(filterEarTag), 300);
    return () => clearTimeout(timer);
  }, [filterEarTag]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCause(filterCause), 300);
    return () => clearTimeout(timer);
  }, [filterCause]);

  const prevDeps = useRef({ debouncedEarTag, debouncedCause, filterDateFrom, filterDateTo });
  const isInitialMount = useRef(true);

  useEffect(() => {
    const filtersChanged = !isInitialMount.current && (
      prevDeps.current.debouncedEarTag !== debouncedEarTag ||
      prevDeps.current.debouncedCause !== debouncedCause ||
      prevDeps.current.filterDateFrom !== filterDateFrom ||
      prevDeps.current.filterDateTo !== filterDateTo
    );
    prevDeps.current = { debouncedEarTag, debouncedCause, filterDateFrom, filterDateTo };
    isInitialMount.current = false;

    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    loadDeaths(currentPage);
  }, [currentPage, debouncedEarTag, debouncedCause, filterDateFrom, filterDateTo]);

  const loadDeaths = async (page: number) => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page };
      if (debouncedEarTag) params.ear_tag = debouncedEarTag;
      if (debouncedCause) params.cause = debouncedCause;
      if (filterDateFrom) params.from_date = filterDateFrom;
      if (filterDateTo) params.to_date = filterDateTo;
      const response = await deathApi.getAll(params);
      setDeaths(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading deaths:', error);
      handleApiError(error, t('error_loading_deaths', 'deaths') || 'Error loading deaths');
    } finally {
      setLoading(false);
    }
  };

  const handleAnimalSelected = (animals: SelectedAnimal[]) => {
    const animal = animals[0];
    const display = `${animal.ear_tag}${animal.breed ? ` - ${animal.breed}` : ''}`;

    if (showAddModal) {
      setNewDeath(prev => ({ ...prev, animal_id: animal.id }));
      setAddAnimalDisplay(display);
    } else if (showEditModal && editingDeath) {
      setEditingDeath(prev => prev ? ({ ...prev, animal_id: animal.id }) : null);
      setEditAnimalDisplay(display);
    }
    setShowAnimalSelector(false);
  };

  const handleAddDeath = async () => {
    try {
      if (!newDeath.animal_id || !newDeath.death_date) {
        showError(t('fill_required_fields', 'deaths') || 'Please fill in all required fields');
        return;
      }

      await deathApi.create(newDeath);
      showSuccess(t('death_added_successfully', 'deaths') || 'Death record added successfully');
      setShowAddModal(false);
      resetNewDeath();
      loadDeaths(currentPage);
    } catch (error) {
      console.error('Error creating death:', error);
      handleApiError(error, t('error_creating_death', 'deaths') || 'Error creating death record');
    }
  };

  const handleEditDeath = async () => {
    try {
      if (!editingDeath || !editingDeath.animal_id || !editingDeath.death_date) {
        showError(t('fill_required_fields', 'deaths') || 'Please fill in all required fields');
        return;
      }

      await deathApi.update(editingDeath.id, editingDeath);
      showSuccess(t('death_updated_successfully', 'deaths') || 'Death record updated successfully');
      setShowEditModal(false);
      setEditingDeath(null);
      loadDeaths(currentPage);
    } catch (error) {
      console.error('Error updating death:', error);
      handleApiError(error, t('error_updating_death', 'deaths') || 'Error updating death record');
    }
  };

  const handleDeleteDeath = async () => {
    try {
      if (!deletingDeath) return;

      await deathApi.delete(deletingDeath.id);
      showSuccess(t('death_deleted_successfully', 'deaths') || 'Death record deleted successfully');
      setShowDeleteModal(false);
      setDeletingDeath(null);
      loadDeaths(currentPage);
    } catch (error) {
      console.error('Error deleting death:', error);
      handleApiError(error, t('error_deleting_death', 'deaths') || 'Error deleting death record');
    }
  };

  const resetNewDeath = () => {
    setNewDeath({
      animal_id: 0,
      death_date: new Date().toISOString().split('T')[0],
      cause: '',
      location: '',
      disposal_method: '',
      veterinary_certificate: '',
      estimated_weight: 0,
      notes: '',
    });
    setAddAnimalDisplay('');
  };

  const openEditModal = (death: Death) => {
    setEditingDeath({ ...death });
    setEditAnimalDisplay(
      death.animal ? `${death.animal.ear_tag}${death.animal.breed ? ` - ${death.animal.breed}` : ''}` : ''
    );
    setShowEditModal(true);
  };

  const openDeleteModal = (death: Death) => {
    setDeletingDeath(death);
    setShowDeleteModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-128px)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">{t('deaths_management', 'deaths') || 'Deaths Management'}</h1>
        <p className="mt-2 text-gray-600">
          {t('deaths_management_desc', 'deaths') || 'Manage animal death records'}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('filters', 'common') || 'Filters'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ear_tag', 'animal') || 'Ear Tag'}
            </label>
            <ClearableInput
              value={filterEarTag}
              onChange={(val) => setFilterEarTag(val)}
              placeholder={t('search_by_ear_tag', 'animal') || 'Search by ear tag'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('cause', 'deaths') || 'Cause'}
            </label>
            <ClearableInput
              value={filterCause}
              onChange={(val) => setFilterCause(val)}
              placeholder={t('search_by_cause', 'deaths') || 'Search by cause'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_from', 'common') || 'Date From'}
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_to', 'common') || 'Date To'}
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
      </div>

      {/* Deaths Table */}
      <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('death_records', 'deaths') || 'Death Records'} ({total})
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>{t('add', 'action') || 'Add'}</span>
          </button>
        </div>

        <div className="overflow-auto flex-1 min-h-0 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-[5]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('death_date', 'deaths') || 'Death Date'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('animal', 'deaths') || 'Animal'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('cause', 'deaths') || 'Cause'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('location', 'deaths') || 'Location'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('estimated_weight', 'deaths') || 'Estimated Weight'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions', 'common') || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deaths.map((death) => (
                <tr key={death.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(death.death_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {death.animal?.ear_tag || '-'}
                    </div>
                    {death.animal?.breed && (
                      <div className="text-xs text-gray-500">{death.animal.breed}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{death.cause || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{death.location || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {death.estimated_weight ? `${death.estimated_weight} kg` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(death)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      {t('edit', 'action') || 'Edit'}
                    </button>
                    <button
                      onClick={() => openDeleteModal(death)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t('delete', 'action') || 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {deaths.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t('no_death_records', 'deaths') || 'No death records'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('no_death_records_desc', 'deaths') || 'Death records will appear here'}
              </p>
            </div>
          )}
        </div>

        {lastPage > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 flex-shrink-0">
            <div className="text-sm text-gray-700">
              {((currentPage - 1) * 50) + 1}–{Math.min(currentPage * 50, total)} / {total}
            </div>
            <Pagination page={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} disabled={loading} />
          </div>
        )}
      </div>

      {/* Add Death Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{t('add_death_record', 'deaths') || 'Add Death Record'}</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewDeath();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Animal Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('animal', 'deaths') || 'Animal'} *
                </label>
                <div className="relative">
                  <div
                    onClick={() => setShowAnimalSelector(true)}
                    className={`w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      newDeath.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {addAnimalDisplay || `${t('select_animal', 'deaths') || 'Select animal'}...`}
                  </div>
                  {newDeath.animal_id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewDeath(prev => ({ ...prev, animal_id: 0 }));
                        setAddAnimalDisplay('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Death Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('death_date', 'deaths') || 'Death Date'} *
                  </label>
                  <input
                    type="date"
                    value={newDeath.death_date || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, death_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                {/* Estimated Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('estimated_weight', 'deaths') || 'Estimated Weight'} (kg)
                  </label>
                  <input
                    type="number"
                    value={newDeath.estimated_weight || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, estimated_weight: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cause */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cause', 'deaths') || 'Cause'}
                  </label>
                  <input
                    type="text"
                    value={newDeath.cause || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, cause: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t('cause_placeholder', 'deaths') || 'e.g., Illness, Accident'}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('location', 'deaths') || 'Location'}
                  </label>
                  <input
                    type="text"
                    value={newDeath.location || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t('location_placeholder', 'deaths') || 'e.g., Stable 1, Box 3'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Disposal Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('disposal_method', 'deaths') || 'Disposal Method'}
                  </label>
                  <input
                    type="text"
                    value={newDeath.disposal_method || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, disposal_method: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t('disposal_method_placeholder', 'deaths') || 'e.g., Cremation, Burial'}
                  />
                </div>

                {/* Veterinary Certificate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('veterinary_certificate', 'deaths') || 'Veterinary Certificate'}
                  </label>
                  <input
                    type="text"
                    value={newDeath.veterinary_certificate || ''}
                    onChange={(e) => setNewDeath(prev => ({ ...prev, veterinary_certificate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t('certificate_number_placeholder', 'deaths') || 'Certificate number'}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notes', 'common') || 'Notes'}
                </label>
                <textarea
                  value={newDeath.notes || ''}
                  onChange={(e) => setNewDeath(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                  placeholder={t('notes_placeholder', 'deaths') || 'Additional information...'}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewDeath();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('cancel', 'action') || 'Cancel'}
              </button>
              <button
                onClick={handleAddDeath}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {t('save', 'action') || 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Death Modal */}
      {showEditModal && editingDeath && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{t('edit_death_record', 'deaths') || 'Edit Death Record'}</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDeath(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Animal Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('animal', 'deaths') || 'Animal'} *
                </label>
                <div className="relative">
                  <div
                    onClick={() => setShowAnimalSelector(true)}
                    className={`w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      editingDeath.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {editAnimalDisplay || `${t('select_animal', 'deaths') || 'Select animal'}...`}
                  </div>
                  {editingDeath.animal_id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingDeath(prev => prev ? ({ ...prev, animal_id: 0 }) : null);
                        setEditAnimalDisplay('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Death Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('death_date', 'deaths') || 'Death Date'} *
                  </label>
                  <input
                    type="date"
                    value={editingDeath.death_date || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, death_date: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                {/* Estimated Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('estimated_weight', 'deaths') || 'Estimated Weight'} (kg)
                  </label>
                  <input
                    type="number"
                    value={editingDeath.estimated_weight || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, estimated_weight: parseFloat(e.target.value) }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cause */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cause', 'deaths') || 'Cause'}
                  </label>
                  <input
                    type="text"
                    value={editingDeath.cause || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, cause: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('location', 'deaths') || 'Location'}
                  </label>
                  <input
                    type="text"
                    value={editingDeath.location || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, location: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Disposal Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('disposal_method', 'deaths') || 'Disposal Method'}
                  </label>
                  <input
                    type="text"
                    value={editingDeath.disposal_method || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, disposal_method: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {/* Veterinary Certificate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('veterinary_certificate', 'deaths') || 'Veterinary Certificate'}
                  </label>
                  <input
                    type="text"
                    value={editingDeath.veterinary_certificate || ''}
                    onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, veterinary_certificate: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notes', 'common') || 'Notes'}
                </label>
                <textarea
                  value={editingDeath.notes || ''}
                  onChange={(e) => setEditingDeath(prev => prev ? ({ ...prev, notes: e.target.value }) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDeath(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('cancel', 'action') || 'Cancel'}
              </button>
              <button
                onClick={handleEditDeath}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {t('update', 'action') || 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animal Selector Modal */}
      <AnimalSelectorModal
        isOpen={showAnimalSelector}
        onClose={() => setShowAnimalSelector(false)}
        onSelect={handleAnimalSelected}
        mode="single"
        title={t('select_animal', 'deaths') || 'Select animal'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingDeath && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-5">
                {t('delete_death_record', 'deaths') || 'Delete Death Record'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {t('delete_death_confirmation', 'deaths') || 'Are you sure you want to delete this death record? This action cannot be undone.'}
                </p>
                {deletingDeath.animal && (
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    {t('animal', 'deaths')}: {deletingDeath.animal.ear_tag}
                  </p>
                )}
              </div>
              <div className="flex justify-center space-x-4 px-4 py-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingDeath(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('cancel', 'action') || 'Cancel'}
                </button>
                <button
                  onClick={handleDeleteDeath}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {t('delete', 'action') || 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deaths;
